import {
  type Message,
  MessageType,
  messageTypeValues,
  type UpdateMessageOrigin,
  originBitRange,
} from '@canvas/protocol-types';

/**
 * Validates that a decoded message has the expected shape for its message type.
 *
 * In the NCS world, the collaboration service is payload-agnostic and will broadcast
 * any data sent by clients without validation. This function ensures the frontend
 * rejects malformed messages before applying them to the local document.
 *
 * Each validator mirrors the corresponding TypeScript type definition in
 * @canvas/protocol-types (e.g. UpdateMessage, LargeUpdateMessage).
 * If a new message type is added or a type's shape changes, the corresponding
 * validator here must be updated to match.
 *
 * @param message The decoded message to validate
 * @returns true if the message has a valid shape, false otherwise
 */
export function isValidReceivedMessage(message: unknown): message is Message {
  if (!isObject(message)) {
    return false;
  }

  if (!isValidMessageType(message.type)) {
    return false;
  }

  const { type, data } = message;

  switch (type) {
    case MessageType.Update:
      return isValidUpdateData(data);
    case MessageType.LargeUpdate:
      return isValidLargeUpdateData(data);
    case MessageType.Awareness:
      return isValidAwarenessData(data);
    case MessageType.SyncInit:
      return isValidSyncInitData(data);
    case MessageType.SyncPhase0:
      return isValidSyncPhase0Data(data);
    case MessageType.SyncPhase1:
      return isValidSyncPhase1Data(data);
    case MessageType.SyncPhase2:
      return isValidSyncPhase2Data(data);
    case MessageType.SessionStatus:
      return isValidSessionStatusData(data);
    case MessageType.Command:
      return isValidCommandData(data);
    case MessageType.BatchedUpdate:
      return isValidBatchedUpdateData(data);
    default:
      return false;
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isValidMessageType(type: unknown): type is MessageType {
  return typeof type === 'number' && messageTypeValues.includes(type as MessageType);
}

function isUint8Array(value: unknown): value is Uint8Array {
  return value instanceof Uint8Array;
}

function isValidOrigin(origin: unknown): origin is UpdateMessageOrigin {
  return typeof origin === 'number' && (origin & ~originBitRange) === 0;
}

function isValidUpdateData(data: unknown): boolean {
  if (!isObject(data)) {
    return false;
  }
  return isUint8Array(data.data) && isValidOrigin(data.origin);
}

function isValidLargeUpdateData(data: unknown): boolean {
  if (!isObject(data)) {
    return false;
  }
  const innerData = data.data;
  if (typeof innerData === 'string') {
    return true;
  }
  if (isObject(innerData)) {
    return typeof innerData.presignedUrl === 'string';
  }
  return false;
}

function isValidAwarenessData(data: unknown): boolean {
  return isObject(data);
}

function isValidSyncInitData(data: unknown): boolean {
  if (!isObject(data)) {
    return false;
  }
  return isUint8Array(data.data);
}

function isValidSyncPhase0Data(data: unknown): boolean {
  if (!isObject(data)) {
    return false;
  }
  return typeof data.recipient === 'string' && isUint8Array(data.request);
}

function isValidSyncPhase1Data(data: unknown): boolean {
  if (!isObject(data)) {
    return false;
  }
  return (
    typeof data.recipient === 'string' &&
    isUint8Array(data.response) &&
    isUint8Array(data.request)
  );
}

function isValidSyncPhase2Data(data: unknown): boolean {
  if (!isObject(data)) {
    return false;
  }
  return typeof data.recipient === 'string' && isUint8Array(data.response);
}

function isValidSessionStatusData(data: unknown): boolean {
  if (!isObject(data)) {
    return false;
  }
  return typeof data.timestamp === 'number' && typeof data.latestStep === 'string';
}

function isValidCommandData(data: unknown): boolean {
  if (!isObject(data)) {
    return false;
  }
  return isUint8Array(data.data);
}

function isValidBatchedUpdateData(data: unknown): boolean {
  if (!isObject(data)) {
    return false;
  }
  const { messages } = data;
  if (!Array.isArray(messages)) {
    return false;
  }
  return messages.every((msg: unknown) => {
    if (!isObject(msg)) {
      return false;
    }
    if (msg.type === MessageType.Update) {
      return isValidUpdateData(msg.data);
    }
    if (msg.type === MessageType.LargeUpdate) {
      return isValidLargeUpdateData(msg.data);
    }
    return false;
  });
}
